import { keyBy, pick, sortBy, uniqBy, zipWith, flatten, concat, groupBy, mapValues } from '@dt/fe-utils/dist/lodash'
import { makeAutoObservable, runInAction } from 'mobx'
import { productExtMobx, RESTAURANT_EVENT, restaurantCenter, restaurantConfig } from '..'
import ProductAPI from 'api/product.api'

class AllMenu {
  constructor () {
    makeAutoObservable(this)

    // 监听 restaurant 初始化
    restaurantCenter.on(RESTAURANT_EVENT.RESTAURANT_INIT, () => {
      if (restaurantConfig.app === 'pos') {
        this.init()
      }
    })

    // 监听 餐品发生 变化
    restaurantCenter.on(RESTAURANT_EVENT.PRODUCT_CHANGED, (data: any) => {
      if (data && data.channel !== '3rdDelivery') {
        this.init()
      }
    })
  }

  /** 菜单 */
  menu: {
    product: any;
    category: any;
  } = {
    product: [],
    category: []
  }

  /** 菜单加载错误 */
  isError = false

  /** 菜单加载中 */
  isLoading = false

  /** 搜索的关键词 */
  searchKey: string | null = null

  /** 当前选中的菜单no */
  selectCategoryNo?: string

  /** 所有的加料项 groups */
  modifierGroups: IModifierGroup[] = []

  /** categoryNo 餐品(simple) map */
  get categoryNoToSimpleProductsMap () {
    const categroyMap: { [categoryNo: string]: ISimpleProduct[] } = {}
    this.simpleProducts?.forEach(simpleProduct => {
      simpleProduct.categories.forEach(categoryNo => {
        if (categroyMap[categoryNo]) {
          categroyMap[categoryNo].push(simpleProduct)
        } else {
          categroyMap[categoryNo] = [simpleProduct]
        }
      })
    })
    return categroyMap
  }

  /** 分类 simple */
  get simpleCategories (): ISimpleCategory[] {
    const simpleCategoryMap: {
      [no: string]: ISimpleCategory;
    } = {}
    this.menu.category.forEach((categroy: any) => {
      if (!simpleCategoryMap[categroy.no]) {
        simpleCategoryMap[categroy.no] = {
          no: categroy.no,
          name: categroy.name
        }
      }
    })
    return Object.values(simpleCategoryMap)
  }

  /** 餐品 simple */
  get simpleProducts (): ISimpleProduct[] {
    return this.menu.product?.map((product: any) => {
      return {
        ...pick(product, ['name', 'categories', 'isNotSingleInCombo', 'no', 'statisticsCategoryNo', 'pinyin', 'allergys', 'tags', 'taxCategoryId', 'taxInPrice']),
        scales: product.scaleGroup.scales
      }
    })
  }

  /** 分类排序 map */
  get categorySortMap () {
    if (productExtMobx.sorts.categories.length) {
      const categorySortMap: any = {}
      productExtMobx.sorts.categories.forEach((d, i) => (categorySortMap[d] = i))
      return categorySortMap
    } else {
      return {}
    }
  }

  /** 分类 */
  get categories (): ISimpleMenuCategory[] {
    const categorySortMap = this.categorySortMap
    const categoryNoToSimpleProductsMap = this.categoryNoToSimpleProductsMap
    const menu = this.simpleCategories.map(category => {
      return { ...category, products: categoryNoToSimpleProductsMap[category.no] }
    })
    // 使用排序规则
    if (productExtMobx.sorts.categories.length) {
      menu.sort((a, b) => (categorySortMap[a.no] || 0) * 1000 - (categorySortMap[b.no] || 0) * 1000)
      return menu
    }
    return menu
  }

  /** 餐品 */
  get products () {
    // 如果是瀑布流则不指定分类显示全餐品，而category类型不指定则显示默认第一个分类
    let products: ISimpleProduct[] = []
    if (this.searchKey === '') {
      return []
    } else if (this.searchKey) {
      return this.simpleProducts.filter(product => ~product.pinyin?.toLowerCase().indexOf(this.searchKey?.toLowerCase() as string))
    }
    if (this.selectCategoryNo) {
      products = this.categories.find(category => category.no === this.selectCategoryNo)?.products || []
    } else {
      products = this.categories.length ? this.categories[0].products : []
    }
    // 为了全菜单和时段菜单的排序一致性，使用id作为默认排序
    products = sortBy(products, 'id')
    // products.sort((a, b) => a.modifyTime < b.modifyTime ? 1 : a.modifyTime === b.modifyTime ? 0 : -1)
    if (this.selectCategoryNo && productExtMobx.sorts.products && productExtMobx.sorts.products[this.selectCategoryNo]) {
      const productSortMap: any = {}
      productExtMobx.sorts.products[this.selectCategoryNo].forEach((d, i) => (productSortMap[d] = i))
      products.sort((a, b) => (productSortMap[a.no] || 0) * 1000 - (productSortMap[b.no] || 0) * 1000)
    }
    return products
  }

  /** 分类no的索引 */
  get noToViewCategoryMap () {
    return keyBy(this.simpleCategories, 'no') as {
      [no: string]: ISimpleCategory;
    }
  }

  /** 餐品no的索引 */
  get noToViewProductMap () {
    return keyBy(this.simpleProducts, 'no')
  }

  /** 餐品规格no的索引 */
  get noToViewProductScaleMap () {
    const productScaleMap: {
      [productScale: string]: {
        name: string;
        scaleName: string;
      };
    } = {}
    this.simpleProducts.forEach(simpleProduct => {
      simpleProduct.scales.forEach(scale => {
        productScaleMap[simpleProduct.no + '_' + scale.no] = {
          name: simpleProduct.name,
          scaleName: simpleProduct.scales.length > 1 ? scale.name : ''
        }
      })
    })
    return productScaleMap
  }

  /** 套餐内餐品 map */
  get notSingleInComboPro () {
    const notSingleInComboPro: any[] = []
    this.simpleProducts.forEach((item: any) => {
      if (!item.isNotSingleInCombo) {
        notSingleInComboPro.push(item)
      }
    })
    return notSingleInComboPro
  }

  /** 根据No获取商品详情 */
  getProductDetail (productNo: string) {
    let detail: any = {}
    this.menu.product.forEach((item: any) => {
      if (productNo && item.no === productNo) {
        detail = item
      }
    })
    return detail
  }

  /** 根据No获取沽清详情 */
  getProductSaleoutDetail (productNo: string) {
    let detail: any = {}
    this.simpleProducts.forEach((item: any) => {
      if (productNo && item.no === productNo) {
        detail = item
      }
    })
    return detail
  }

  /** 选择分类 */
  selectCategory (category?: ISimpleMenuCategory | string) {
    this.selectCategoryNo = (typeof category === 'string' || typeof category === 'undefined') ? category : category.no
  }

  /** 将所有的口味加料项映射成map */
  keyByModify (key: (item: any) => string) {
    return keyBy(flatten(zipWith(this.modifierGroups, item => item.modifiers.map(i => ({ ...i, groupNo: item.no })))), key)
  }

  /** 初始化 */
  init () {
    this.isLoading = true
    this.isError = false
    ProductAPI.getStoreAllProducts().then((data: { modifierGroup: IModifierGroup[]; no: string; scaleGroup: IScaleGroup }[]) => {
      runInAction(() => {
        const arr: IModifierGroup[] = (flatten(zipWith(data, (arg) => concat(arg.modifierGroup, flatten(zipWith((arg?.scaleGroup?.scales || []), (item) => item.modifierGroups))))) as IModifierGroup[])
        const group = mapValues(groupBy(arr, 'no'), (item) => uniqBy(flatten(zipWith(item.filter(i => i.modifiers), (i) => i.modifiers)), 'no'))
        this.modifierGroups = uniqBy(arr, 'no').map((item) => ({ ...item, modifiers: group[item.no] }))
      })
    })
    return ProductAPI.getAllProduct().then((data: any) => {
      runInAction(() => {
        this.menu = data
        this.isLoading = false
      })
    }, () => {
      runInAction(() => {
        this.isError = true
        this.isLoading = false
      })
    })
  }

  /** 设置搜索关键词 */
  setSearchKey =(searchKey: string | null) => {
    this.searchKey = searchKey
  }
}

export default new AllMenu()
